/**
 * Eco Event Logger
 * Phase P2.2-10: Sustainability Engine v1
 * Phase P2.3-2: Sustainability Impact Dashboard (cache invalidation)
 *
 * Frontend-based logging system for all sustainability actions.
 * Creates EcoEvent records for activity feed and future P3 dashboards.
 */

import type { EcoEvent, EcoSavings, RoomServiceMode } from './greenStay';
import { estimateSavings } from './greenStay';
import { invalidateSustainabilityCache } from './sustainabilityAggregator';

// =============================================================================
// IN-MEMORY STORE WITH LOCALSTORAGE PERSISTENCE
// =============================================================================

const ECO_EVENTS_STORAGE_KEY = 'hotel-concierge-eco-events';
const EVENT_ID_COUNTER_KEY = 'hotel-concierge-eco-event-counter';

let ecoEvents: EcoEvent[] = [];
let eventIdCounter = 1;

/**
 * Load eco events from localStorage on initialization
 */
function loadEcoEvents(): void {
  try {
    const stored = localStorage.getItem(ECO_EVENTS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      ecoEvents = parsed.map((e: any) => ({
        ...e,
        createdAt: new Date(e.createdAt),
      }));
      console.log(`[EcoLogger] Loaded ${ecoEvents.length} eco events from storage`);
    }

    const counterStored = localStorage.getItem(EVENT_ID_COUNTER_KEY);
    if (counterStored) {
      eventIdCounter = parseInt(counterStored, 10);
    }
  } catch (error) {
    console.error('[EcoLogger] Failed to load eco events from storage:', error);
  }
}

/**
 * Save eco events to localStorage
 */
function saveEcoEvents(): void {
  try {
    const serializedEvents = JSON.stringify(ecoEvents);

    localStorage.setItem(ECO_EVENTS_STORAGE_KEY, serializedEvents);
    localStorage.setItem(EVENT_ID_COUNTER_KEY, eventIdCounter.toString());

    console.log('[EcoLogger] Saved eco events to localStorage:', ecoEvents.length, 'events');
    console.log('[EcoLogger] Latest event:', ecoEvents[ecoEvents.length - 1]);

    // Invalidate sustainability metrics cache
    invalidateSustainabilityCache();

    // Dispatch custom event for same-window updates
    window.dispatchEvent(new CustomEvent('eco-events-updated', {
      detail: { events: ecoEvents }
    }));

    // Note: Native storage events fire automatically in OTHER windows when localStorage changes.
    // We don't need to manually dispatch them. They won't fire in the current window.
    console.log('[EcoLogger] Storage updated, other windows should receive storage event automatically');
  } catch (error) {
    console.error('[EcoLogger] Failed to save eco events to storage:', error);
  }
}

// Load on module initialization
loadEcoEvents();

// Listen for storage changes from other windows/tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === ECO_EVENTS_STORAGE_KEY && e.newValue) {
      console.log('[EcoLogger] Storage change detected from another window, reloading events');
      loadEcoEvents();
    }
  });

  // Listen for same-window updates
  window.addEventListener('eco-events-updated', () => {
    console.log('[EcoLogger] Same-window update detected');
    // No need to reload - already updated by saveEcoEvents()
  });
}

/**
 * Generate unique event ID
 */
function generateEventId(): string {
  return `eco-${Date.now()}-${eventIdCounter++}`;
}

// =============================================================================
// ECO EVENT CREATION
// =============================================================================

/**
 * Log eco mode enabled
 */
export function logEcoModeEnabled(
  roomNumber: string,
  stayId: string
): EcoEvent {
  const event: EcoEvent = {
    eventId: generateEventId(),
    roomNumber,
    stayId,
    date: new Date().toISOString().split('T')[0]!, // Today
    serviceMode: 'FULL_CLEAN' as RoomServiceMode, // Default, no service change yet
    action: 'ECO_ENABLED',
    estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 }, // No immediate savings
    createdAt: new Date(),
  };

  ecoEvents.push(event);
  saveEcoEvents();
  console.log('[EcoLogger] Eco Mode Enabled:', event);
  return event;
}

/**
 * Log eco mode disabled
 */
export function logEcoModeDisabled(
  roomNumber: string,
  stayId: string
): EcoEvent {
  const event: EcoEvent = {
    eventId: generateEventId(),
    roomNumber,
    stayId,
    date: new Date().toISOString().split('T')[0]!,
    serviceMode: 'FULL_CLEAN' as RoomServiceMode, // Restored to full clean
    action: 'ECO_DISABLED',
    estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
    createdAt: new Date(),
  };

  ecoEvents.push(event);
  saveEcoEvents();
  console.log('[EcoLogger] Eco Mode Disabled:', event);
  return event;
}

/**
 * Log skip cleaning scheduled for specific date
 */
export function logSkipCleaning(
  roomNumber: string,
  stayId: string,
  date: string,
  savings: EcoSavings = estimateSavings('NO_SERVICE' as RoomServiceMode)
): EcoEvent {
  const event: EcoEvent = {
    eventId: generateEventId(),
    roomNumber,
    stayId,
    date,
    serviceMode: 'NO_SERVICE' as RoomServiceMode,
    action: 'SKIP_CLEANING',
    estimatedSavings: savings,
    createdAt: new Date(),
  };

  ecoEvents.push(event);
  saveEcoEvents();
  console.log('[EcoLogger] Skip Cleaning:', event);
  return event;
}

/**
 * Log towels only scheduled for specific date
 */
export function logTowelsOnly(
  roomNumber: string,
  stayId: string,
  date: string,
  savings: EcoSavings = estimateSavings('TOWELS_ONLY' as RoomServiceMode)
): EcoEvent {
  const event: EcoEvent = {
    eventId: generateEventId(),
    roomNumber,
    stayId,
    date,
    serviceMode: 'TOWELS_ONLY' as RoomServiceMode,
    action: 'TOWELS_ONLY',
    estimatedSavings: savings,
    createdAt: new Date(),
  };

  ecoEvents.push(event);
  saveEcoEvents();
  console.log('[EcoLogger] Towels Only:', event);
  return event;
}

/**
 * Log full clean restored for specific date
 */
export function logFullCleanRestored(
  roomNumber: string,
  stayId: string,
  date: string
): EcoEvent {
  const event: EcoEvent = {
    eventId: generateEventId(),
    roomNumber,
    stayId,
    date,
    serviceMode: 'FULL_CLEAN' as RoomServiceMode,
    action: 'FULL_CLEAN_RESTORED',
    estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
    createdAt: new Date(),
  };

  ecoEvents.push(event);
  saveEcoEvents();
  console.log('[EcoLogger] Full Clean Restored:', event);
  return event;
}

/**
 * Log consolidated Green Stay preferences update
 * Creates activities for eco mode changes and each date-specific service change
 */
export function logGreenStayUpdate(
  roomNumber: string,
  stayId: string,
  changes: {
    ecoModeChanged?: { from: boolean; to: boolean };
    skipCleaningDates?: string[];
    towelsOnlyDates?: string[];
  }
): EcoEvent {
  const createdAt = new Date();
  const today = new Date().toISOString().split('T')[0]!;
  let primaryEvent: EcoEvent | null = null;

  // REMOVE ALL OLD GREEN STAY EVENTS FOR THIS ROOM (today and future)
  // This ensures we replace ALL eco cleaning preferences, not just the ones in the new arrays
  // Keep past events for historical tracking
  ecoEvents = ecoEvents.filter(e => {
    // Keep if different room/stay
    if (e.roomNumber !== roomNumber || e.stayId !== stayId) return true;

    // Keep if it's a past date (for historical tracking)
    if (e.date < today) return true;

    // Keep if it's not a green stay event (SKIP_CLEANING or TOWELS_ONLY)
    if (e.action !== 'SKIP_CLEANING' && e.action !== 'TOWELS_ONLY') return true;

    // Remove - we're replacing ALL green stay preferences for today/future
    console.log('[EcoLogger] Removing old green stay event:', { date: e.date, serviceMode: e.serviceMode, action: e.action });
    return false;
  });

  // If eco mode changed, create an event for that
  if (changes.ecoModeChanged) {
    const action = changes.ecoModeChanged.to ? 'ECO_ENABLED' : 'ECO_DISABLED';
    primaryEvent = {
      eventId: generateEventId(),
      roomNumber,
      stayId,
      date: today,
      serviceMode: 'FULL_CLEAN' as RoomServiceMode,
      action: action,
      estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
      createdAt,
    };
    ecoEvents.push(primaryEvent);
  } else {
    // Even if eco mode didn't change, check if we need to ensure an ECO_ENABLED event exists
    // This handles cases where eco mode is ON but the event was never logged
    const hasEcoEnabledEvent = ecoEvents.some(e =>
      e.roomNumber === roomNumber &&
      e.stayId === stayId &&
      e.action === 'ECO_ENABLED'
    );

    // If skip/towels dates exist but no ECO_ENABLED event, create one
    if (!hasEcoEnabledEvent && (changes.skipCleaningDates?.length || changes.towelsOnlyDates?.length)) {
      primaryEvent = {
        eventId: generateEventId(),
        roomNumber,
        stayId,
        date: today,
        serviceMode: 'FULL_CLEAN' as RoomServiceMode,
        action: 'ECO_ENABLED',
        estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
        createdAt,
      };
      ecoEvents.push(primaryEvent);
      console.log('[EcoLogger] Created missing ECO_ENABLED event for room', roomNumber);
    }
  }

  // IMPORTANT: A date can be in BOTH arrays in the UI (towels only is a subset of skip cleaning)
  // But we should only create ONE event per date, prioritizing TOWELS_ONLY over SKIP_CLEANING
  const towelsOnlySet = new Set(changes.towelsOnlyDates || []);

  // Create events for skip cleaning dates (excluding towels only dates)
  if (changes.skipCleaningDates && changes.skipCleaningDates.length > 0) {
    changes.skipCleaningDates.forEach((date) => {
      // Skip if this date is also in towelsOnlyDates - we'll create that event instead
      if (towelsOnlySet.has(date)) {
        console.log('[EcoLogger] Skipping SKIP_CLEANING for', date, '- will create TOWELS_ONLY instead');
        return;
      }

      const event: EcoEvent = {
        eventId: generateEventId(),
        roomNumber,
        stayId,
        date,
        serviceMode: 'NO_SERVICE' as RoomServiceMode,
        action: 'SKIP_CLEANING',
        estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
        createdAt,
      };
      ecoEvents.push(event);
      if (!primaryEvent) primaryEvent = event;
    });
  }

  // Create events for towels only dates
  if (changes.towelsOnlyDates && changes.towelsOnlyDates.length > 0) {
    changes.towelsOnlyDates.forEach((date) => {
      const event: EcoEvent = {
        eventId: generateEventId(),
        roomNumber,
        stayId,
        date,
        serviceMode: 'TOWELS_ONLY' as RoomServiceMode,
        action: 'TOWELS_ONLY',
        estimatedSavings: { water: 0, laundry: 0, energy: 0, co2: 0 },
        createdAt,
      };
      ecoEvents.push(event);
      if (!primaryEvent) primaryEvent = event;
    });
  }

  saveEcoEvents();
  console.log('[EcoLogger] Green Stay Update - Events created:', ecoEvents.filter(e => e.createdAt === createdAt), 'Total events now:', ecoEvents.length, 'Changes:', changes);
  return primaryEvent!;
}

// =============================================================================
// ECO EVENT QUERIES
// =============================================================================

/**
 * Get all eco events
 */
export function getAllEcoEvents(): EcoEvent[] {
  console.log('[EcoLogger] getAllEcoEvents() called, returning', ecoEvents.length, 'events');
  return [...ecoEvents].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/**
 * Get eco events for specific stay
 */
export function getEcoEventsByStay(stayId: string): EcoEvent[] {
  return ecoEvents
    .filter(e => e.stayId === stayId)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/**
 * Get eco events for specific room
 */
export function getEcoEventsByRoom(roomNumber: string): EcoEvent[] {
  return ecoEvents
    .filter(e => e.roomNumber === roomNumber)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/**
 * Get recent eco events (last N)
 */
export function getRecentEcoEvents(limit: number = 20): EcoEvent[] {
  return getAllEcoEvents().slice(0, limit);
}

/**
 * Get eco events by date range
 */
export function getEcoEventsByDateRange(startDate: string, endDate: string): EcoEvent[] {
  return ecoEvents
    .filter(e => e.date >= startDate && e.date <= endDate)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

// =============================================================================
// ANALYTICS
// =============================================================================

/**
 * Calculate total savings from all eco events
 */
export function calculateTotalSavings(events: EcoEvent[]): EcoSavings {
  return events.reduce(
    (total, event) => ({
      water: total.water + event.estimatedSavings.water,
      laundry: total.laundry + event.estimatedSavings.laundry,
      energy: total.energy + event.estimatedSavings.energy,
      co2: total.co2 + event.estimatedSavings.co2,
    }),
    { water: 0, laundry: 0, energy: 0, co2: 0 }
  );
}

/**
 * Get savings for specific stay
 */
export function getStaySavings(stayId: string): EcoSavings {
  const stayEvents = getEcoEventsByStay(stayId);
  return calculateTotalSavings(stayEvents);
}

/**
 * Get savings for specific room
 */
export function getRoomSavings(roomNumber: string): EcoSavings {
  const roomEvents = getEcoEventsByRoom(roomNumber);
  return calculateTotalSavings(roomEvents);
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Clear all eco events (for testing/reset)
 */
export function clearAllEcoEvents(): void {
  ecoEvents = [];
  eventIdCounter = 1;
  saveEcoEvents();
  console.log('[EcoLogger] All eco events cleared');
}

// Expose clear function globally for dev tools
if (typeof window !== 'undefined') {
  (window as any).clearEcoEvents = clearAllEcoEvents;
  console.log('[EcoLogger] To clear all eco events, run: clearEcoEvents()');
}

/**
 * Export eco events as JSON (for P3 persistence)
 */
export function exportEcoEvents(): string {
  return JSON.stringify(ecoEvents, null, 2);
}

/**
 * Import eco events from JSON (for P3 persistence)
 */
export function importEcoEvents(json: string): void {
  try {
    const imported = JSON.parse(json);
    if (Array.isArray(imported)) {
      ecoEvents = imported.map(e => ({
        ...e,
        createdAt: new Date(e.createdAt),
      }));
      saveEcoEvents();
      console.log(`[EcoLogger] Imported ${ecoEvents.length} eco events`);
    }
  } catch (error) {
    console.error('[EcoLogger] Failed to import eco events:', error);
  }
}

/**
 * Get event count
 */
export function getEcoEventCount(): number {
  return ecoEvents.length;
}

/**
 * Check if event exists for specific date and action (prevent duplicates)
 */
export function hasEcoEventForDate(
  stayId: string,
  date: string,
  action: EcoEvent['action']
): boolean {
  return ecoEvents.some(e =>
    e.stayId === stayId &&
    e.date === date &&
    e.action === action
  );
}
